import Loading from '#components/a/Loading'
import ErrorModal from '#components/m/ErrorModal'
import NotificationModal from '#components/m/NotificationModal'
import {routeHelper} from '#config/routeKeys'
import Api from '#services/api'
import {statusTypes} from 'ag-common/tools/fileStatus'
import i18n from 'i18next'
import PropTypes from 'prop-types'
import React, {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import actions from 'redux-auto'
import NachweisDetailHeader from './NachweisDetailHeader'
import NachweisDetailTaetigkeiten from './NachweisDetailTaetigkeiten'

export default function NachweisDetailEdit(props) {
  const [hasChanges, setHasChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [ausbildungsnachweise, setAusbildungsnachweise] = useState([])
  const [nachweis, setNachweis] = useState(null)
  const [firstNachweisNotificationClose, setFirstNachweisNotificationClose] = useState(false)
  const [isAbteilungsNameInvalid, setIsAbteilungsNameInvalid] = useState(false)
  const [disableAddTaetigkeit, setDisableAddTaetigkeit] = useState(false)
  const [errorTitle, setErrorTitle] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  const getNachweisFromApi = async nachweisId => await Api.readNachweisDetails(nachweisId)
  const isNewNachweis = nachweis => nachweis === null || nachweis.id === null || nachweis.id === undefined

  const {nachweisId, hasVersetzungsplan} = props

  const navigate = useNavigate()
  useEffect(() => {
    const fetchNewNachweis = async () => {
      try {
        const nachweisFromApi = nachweisId ? await getNachweisFromApi(nachweisId) : await createNewNachweis()
        setNachweis(nachweisFromApi)

        if (hasVersetzungsplan) {
          setIsAbteilungsNameInvalid(false)
          setDisableAddTaetigkeit(false)
        } else {
          setIsAbteilungsNameInvalid(
            nachweisFromApi.abteilungsname === undefined || nachweisFromApi.abteilungsname === '',
          )
          setDisableAddTaetigkeit(nachweisId === null)
        }

        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
        if (error.message === 'api_get_nachweis_foreign_abteilung') {
          setErrorMessage(error.message)
          setErrorTitle('api_get_nachweis_foreign_abteilung_title')
        } else {
          actions.error.writeError({error})
        }
      }
    }

    fetchNewNachweis()
  }, [nachweisId, hasVersetzungsplan])

  const createNewNachweis = async () => {
    const overviewModel = await Api.readNachweisList()
    const nachweise = overviewModel[0].ausbildungsnachweise
    nachweise.forEach(nachweis => {
      if (nachweis.nummer == props.nachweisNumber && nachweis.id) {
        navigate(routeHelper.ausbildungsnachweisDetail(nachweis.id))
        return
      }
    })

    setAusbildungsnachweise(nachweise)
    return {
      id: null,
      nummer: props.nachweisNumber,
      userId: props.authorId,
      taetigkeiten: [],
      status: statusTypes.draft,
      typ: props.nachweisTyp,
      ausbildungsberuf: props.ausbildungsberuf,
    }
  }

  const handleAbteilungsChange = async abteilung => {
    try {
      setIsSaving(true)

      if (isNewNachweis(nachweis)) {
        const createdNachweis = await Api.createNachweis(nachweis.nummer, abteilung.name)
        await Api.updateAusbildungsnachweisAbteilungById(createdNachweis.id, abteilung.id)
        setNachweis({
          ...createdNachweis,
          abteilungsname: abteilung.name,
          abteilungsId: abteilung.id,
        })
        navigate(routeHelper.ausbildungsnachweisDetail(createdNachweis.id))
      } else {
        await Api.updateAusbildungsnachweisAbteilungById(nachweis.id, abteilung.id)

        setNachweis({
          ...nachweis,
          abteilungsname: abteilung.name,
          abteilungsId: abteilung.id,
        })
      }
    } catch (error) {
      actions.error.writeError({error})
    } finally {
      setIsSaving(false)
    }
  }

  const handleAbteilungsNameChange = async (abteilungsname, isNameInvalid) => {
    setNachweis({
      ...nachweis,
      abteilungsname,
    })
    setIsAbteilungsNameInvalid(isNameInvalid)
    setDisableAddTaetigkeit(isNameInvalid)

    if (!isNewNachweis(nachweis) && !isNameInvalid) {
      try {
        setIsSaving(true)
        await Api.updateAusbildungsnachweisAbteilungByName(nachweis.id, abteilungsname)
      } catch (error) {
        actions.error.writeError({error})
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleEinreichen = async () => {
    try {
      const result = await Api.nachweisEinreichen(nachweis.id)
      setNachweis(result)
    } catch (error) {
      actions.error.writeError({error})
    }
  }

  const handleFreigeben = async comment => {
    try {
      const result = await Api.nachweisFreigeben(nachweis.id, comment)
      setNachweis({
        ...result,
      })
      navigate(-1)
    } catch (error) {
      actions.error.writeError({error})
    }
  }

  const handleAblehnen = async comment => {
    try {
      const result = await Api.nachweisAblehnen(nachweis.id, comment)
      setNachweis({
        ...result,
      })
      navigate(-1)
    } catch (error) {
      actions.error.writeError({error})
    }
  }

  const handleFreigabeZurueckziehen = async () => {
    try {
      const result = await Api.nachweisFreigabeZurueckziehen(nachweis.id)
      setNachweis(result)
    } catch (error) {
      actions.error.writeError({error})
    }
  }

  const mapDeleteTaetigkeiten = (nachweis, taetigkeitId) => {
    return nachweis.taetigkeiten.filter(taetigkeit => taetigkeit.id !== taetigkeitId)
  }

  const handleDeleteTaetigkeit = async taetigkeitId => {
    setIsSaving(true)
    try {
      await Api.deleteTaetigkeit(taetigkeitId)

      const updatedNachweis = await getNachweisFromApi(nachweis.id)
      setNachweis(updatedNachweis)
      setIsSaving(false)
    } catch (error) {
      actions.error.writeError({error})
      setIsSaving(false)
    }
  }

  const handleAddTaetigkeit = async taetigkeitGruppe => {
    try {
      setDisableAddTaetigkeit(true)
      if (isNewNachweis(nachweis)) {
        const createdNachweis = await Api.createNachweis(nachweis.nummer, nachweis.abteilungsname)
        const result = await Api.createTaetigkeit(createdNachweis.id, undefined, taetigkeitGruppe, '', 0, 0)
        setNachweis({
          ...createdNachweis,
          taetigkeiten: [...(createdNachweis.taetigkeiten || []), result],
        })
      } else {
        const result = await Api.createTaetigkeit(nachweis.id, undefined, taetigkeitGruppe, '', 0, 0)

        setNachweis({
          ...nachweis,
          taetigkeiten: [...(nachweis.taetigkeiten || []), result],
        })
      }
      setDisableAddTaetigkeit(false)
    } catch (error) {
      setDisableAddTaetigkeit(false)
      actions.error.writeError({error})
    }
  }

  const handleUpdateTaetigkeit = async taetigkeit => {
    setIsSaving(true)
    try {
      await Api.updateTaetigkeit(
        nachweis.id,
        taetigkeit.id,
        taetigkeit.text,
        taetigkeit.stunden,
        taetigkeit.minuten,
        taetigkeit.qualifikationen,
      )
      const updatedNachweis = await getNachweisFromApi(nachweis.id)
      setNachweis(updatedNachweis)
      setIsSaving(false)
      setHasChanges(false)
    } catch (error) {
      const isNotFound = error.message === 'nachweis_not_found'
      setIsSaving(false)
      setHasChanges(false)
      setErrorTitle(isNotFound ? 'nachweis_not_found_title' : 'ups')
      setErrorMessage(error.message)
    }
  }

  const handleHasFormChanges = updatedTaetigkeit => {
    if (!updatedTaetigkeit || !updatedTaetigkeit.id) {
      return
    }
    setNachweis(prevNachweis => {
      return {
        ...prevNachweis,
        taetigkeiten: prevNachweis.taetigkeiten.map(t => (t.id === updatedTaetigkeit.id ? updatedTaetigkeit : t)),
      }
    })
    setHasChanges(true)
  }

  const handleUploadFile = async (e, gruppe) => {
    const file = e?.target?.files && e.target.files[0]
    try {
      setIsSaving(true)
      const uploadedFile = await Api.uploadFile(file)
      const updatedNachweis = await Api.saveAttachmentToNachweis(nachweis.id, uploadedFile.blobId, gruppe, file.type)
      setIsSaving(false)
      setNachweis(updatedNachweis)
    } catch (error) {
      setIsSaving(false)
      setErrorTitle('misc_generic_error')
      setErrorMessage(error.message)
    }
  }

  const renderErrorModal = () => (
    <ErrorModal
      headline={i18n.t(errorTitle)}
      text={i18n.t(errorMessage)}
      showModal={!!errorMessage}
      buttonText={i18n.t('okay')}
      onButtonClick={() => {
        setErrorMessage(null)
      }}
    />
  )

  const handleDeleteFile = async blobId => {
    setIsSaving(true)
    try {
      await Api.deleteAttachmentFromNachweis(nachweis.id, blobId)
      setNachweis({
        ...nachweis,
        anhaenge: nachweis.anhaenge.filter(x => x.blobId !== blobId),
      })
      setIsSaving(false)
    } catch (error) {
      actions.error.writeError({error})
      setIsSaving(false)
    }
  }

  const closeFirstNachweisNotification = () => {
    setFirstNachweisNotificationClose(true)
  }

  const mapUpdateTaetigkeiten = (nachweis, updatedTaetigkeit) =>
    nachweis.taetigkeiten.map(taetigkeit => {
      if (taetigkeit.id === updatedTaetigkeit.id) {
        return {
          ...taetigkeit,
          text: updatedTaetigkeit.text,
          stunden: updatedTaetigkeit.stunden,
          minuten: updatedTaetigkeit.minuten,
          qualifikationen: updatedTaetigkeit.qualifikationen,
        }
      }
      return taetigkeit
    })

  const showFirstNachweisNotification =
    ausbildungsnachweise.length === 0 && !firstNachweisNotificationClose && isNewNachweis(nachweis)
  const {permissions, isModalView, authorId} = props

  const hasTaetigkeitWithText =
    nachweis?.taetigkeiten?.length > 0 && nachweis?.taetigkeiten?.some(taetigkeit => taetigkeit.text !== '')

  if (errorMessage !== null) {
    return renderErrorModal()
  }

  if (isLoading || !nachweis) return <Loading> </Loading>
  return (
    <div className="nachweisdetail">
      <NachweisDetailHeader
        ausbildungsnachweise={ausbildungsnachweise}
        isNewNachweis={isNewNachweis(nachweis)}
        onNachweisAbteilungChange={handleAbteilungsChange}
        onNachweisAbteilungNameChange={handleAbteilungsNameChange}
        handleEinreichenButton={handleEinreichen}
        handleFreigabeZurueckziehenButton={handleFreigabeZurueckziehen}
        hasChanges={hasChanges}
        isSaving={isSaving}
        nachweis={nachweis}
        authorId={authorId || nachweis.userId}
        disablePrintButton={isModalView}
        permissions={permissions}
        suppressEmptyAbteilungInputError={showFirstNachweisNotification}
        hasTaetigkeitWithText={hasTaetigkeitWithText}
      />
      <NachweisDetailTaetigkeiten
        handleAddTaetigkeit={handleAddTaetigkeit}
        handleDeleteTaetigkeit={handleDeleteTaetigkeit}
        handleUpdateTaetigkeit={handleUpdateTaetigkeit}
        handleChangeTaetigkeit={handleHasFormChanges}
        handleUploadFile={handleUploadFile}
        handleDeleteFile={handleDeleteFile}
        nachweis={nachweis}
        nachweisTyp={nachweis.typ}
        isNewNachweis={isNewNachweis(nachweis)}
        permissions={permissions}
        userId={nachweis.userId}
        disableAddTaetigkeit={disableAddTaetigkeit}
        handleFreigeben={handleFreigeben}
        handleAblehnen={handleAblehnen}
      />
      <NotificationModal
        modifier="notification"
        headline={i18n.t('login_title')}
        message={`${i18n.t('first_login_message') + i18n.t(nachweis.typ)}.`}
        buttonText={i18n.t('all_right')}
        showModal={showFirstNachweisNotification}
        onButtonClick={closeFirstNachweisNotification}
      />
    </div>
  )
}

NachweisDetailEdit.propTypes = {
  nachweisId: PropTypes.string,
  nachweisTyp: PropTypes.string,
  isModalView: PropTypes.bool,
  authorId: PropTypes.number,
  hasVersetzungsplan: PropTypes.bool,
  permissions: PropTypes.object.isRequired,
  ausbildungsberuf: PropTypes.number,
  nachweisNumber: PropTypes.number,
}
